'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FloatingButton } from '@/app/lcv/components/campaigns/floating-button';
import { EngineStory } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/engine-story';
import { IntroEngineReveal } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/intro-engine-reveal';
import { LoadingVeil } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/loading-veil';
import { PlayPrompt } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/play-prompt';
import { INTRO_INDEX, SPARTAN_ENGINE_SECTION_COUNT, SPARTAN_ENGINE_SECTION_IDS, SPEED_INDEX, STORY_INDEX } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/constants';
import { PaginationBullets } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/pagination-bullets';
import { type SectionSliderProps } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/types';
import { msLeftSince } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/pageClock';
import type { HeroSlidePlayback, RegisterHeroPlayUnlock, SectionPhase } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/types';
import { SpeedKeyMessages } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/speed-key-messages';
import { HERO_BUTTON_CLASSNAME, SECTION_MIN_HEIGHT_CLASSNAME, SECTION_VIDEO_BOX_CLASSNAME } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/styles';
import { SPARTAN_ENGINE_HERO_DATA_GA, SPARTAN_ENGINE_HERO_SKIP_LABEL, SPARTAN_ENGINE_HERO_TRACKING } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/tracking';
import { SECTION_FADE_SECONDS, SECTION_VIDEO_LOAD_TIMEOUT_SECONDS, VIDEO_COVER_FALLBACK_SECONDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';
import { SPARTAN_ENGINE_EVENT_NAME } from '@/app/lcv/components/sections/spartan/engine/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { reportRumWarning } from '@/app/utils/reportRum';
import logger from '@/lib/logger';
SECTION_MIN_HEIGHT_CLASSNAME;
export const SectionSlider = t0 => {
  const $ = _c(238);
  const {
    shouldRenderFallback: t1,
    activeIndex,
    exitingIndex,
    playMode,
    videoProgressRef,
    generation,
    isLocked,
    isSkipCovered,
    onSkip,
    onVideoLoadTimeout,
    onVideoBlocked,
    isPlayPromptShown,
    isAutoplayChecked,
    onPlayPromptAccepted,
    clockStartMs,
    reportAnimationEnd,
    reportExitEnd,
    onSelectSection,
    onIntroVideoEnded,
    onIntroSequenceComplete,
    onIntroFloatCycle,
    storyBreathingAnimationRef,
    onBlackModeChange,
    onHotspotPopupChange,
    onVideoReady,
    onVideoError,
    onIntroImageLoaded,
    readyToPlay,
    canvasBoxRef,
    introFloatAnimationRef,
    engineAnchorRef,
    engineAuraVisibilityRef,
    storyVideoGeneration,
    onStoryClipsComplete,
    haveStoryClipsFinished,
    isSectionInView,
    className
  } = t0;
  const shouldRenderFallback = t1 === undefined ? false : t1;
  const [storyPictureGeneration, setStoryPictureGeneration] = useState(null);
  const [storyCoveredGeneration, setStoryCoveredGeneration] = useState(null);
  const [isIntroSequenceComplete, setIsIntroSequenceComplete] = useState(false);
  const [haveBulletsAppeared, setHaveBulletsAppeared] = useState(false);
  const [videoEndedVisit, setVideoEndedVisit] = useState(null);
  const [canLoadSpeedMain, setCanLoadSpeedMain] = useState(false);
  const [coverFallbackVisit, setCoverFallbackVisit] = useState(null);
  const [bufferingIndex, setBufferingIndex] = useState(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [playbackStartedGeneration, setPlaybackStartedGeneration] = useState(t2);
  let t3;
  if ($[1] !== generation) {
    t3 = index => `${index}:${generation[index]}`;
    $[1] = generation;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const visitKey = t3;
  let t4;
  if ($[3] !== visitKey) {
    t4 = index_0 => setVideoEndedVisit(visitKey(index_0));
    $[3] = visitKey;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const markVideoEnded = t4;
  let t5;
  if ($[5] !== generation) {
    t5 = index_1 => setPlaybackStartedGeneration(started => started[index_1] === generation[index_1] ? started : {
      ...started,
      [index_1]: generation[index_1]
    });
    $[5] = generation;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const markPlaybackStarted = t5;
  const hasActiveVideoEnded = videoEndedVisit === visitKey(activeIndex);
  const hasActivePlaybackStarted = playbackStartedGeneration[activeIndex] === generation[activeIndex];
  const isAwaitingPlayback = isSectionInView && !isPlayPromptShown && playMode === "full" && exitingIndex === null && !isSkipCovered && !hasActivePlaybackStarted && !hasActiveVideoEnded;
  const activeVisitKey = visitKey(activeIndex);
  const hasCoverFallback = coverFallbackVisit === activeVisitKey;
  const isClipLoading = readyToPlay !== false && !isPlayPromptShown && playMode === "full" && !hasActiveVideoEnded && (isAwaitingPlayback && hasCoverFallback || bufferingIndex === activeIndex);
  const showSkipButton = readyToPlay !== false && hasActivePlaybackStarted && !isPlayPromptShown && !hasActiveVideoEnded && playMode === "full" && exitingIndex === null && !isSkipCovered;
  let t6;
  let t7;
  if ($[7] !== activeIndex || $[8] !== playMode) {
    t6 = () => {
      if (activeIndex !== INTRO_INDEX || playMode === "final") {
        setCanLoadSpeedMain(true);
      }
    };
    t7 = [activeIndex, playMode];
    $[7] = activeIndex;
    $[8] = playMode;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  const preloadStory = activeIndex === STORY_INDEX;
  const storyVisit = generation[STORY_INDEX];
  const hasStoryPicture = playMode === "final" || storyPictureGeneration === storyVisit || activeIndex === STORY_INDEX && hasCoverFallback;
  const holdSpeed = activeIndex === STORY_INDEX && playMode === "full" && storyCoveredGeneration !== storyVisit;
  const isRingTrackingClip = playMode === "full" && activeIndex !== INTRO_INDEX && !haveStoryClipsFinished;
  const isProgressWatched = isSectionInView && isRingTrackingClip;
  let t8;
  if ($[11] !== markPlaybackStarted || $[12] !== storyVisit) {
    t8 = () => {
      setStoryPictureGeneration(storyVisit);
      markPlaybackStarted(STORY_INDEX);
    };
    $[11] = markPlaybackStarted;
    $[12] = storyVisit;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleStoryPicture = t8;
  let t9;
  if ($[14] !== markPlaybackStarted) {
    t9 = () => {
      markPlaybackStarted(SPEED_INDEX);
    };
    $[14] = markPlaybackStarted;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleSpeedPlayback = t9;
  let t10;
  if ($[16] !== reportAnimationEnd) {
    t10 = () => reportAnimationEnd(INTRO_INDEX);
    $[16] = reportAnimationEnd;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const reportIntroEnd = t10;
  let t11;
  if ($[18] !== reportAnimationEnd) {
    t11 = () => reportAnimationEnd(STORY_INDEX);
    $[18] = reportAnimationEnd;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const reportStoryEnd = t11;
  let t12;
  if ($[20] !== reportAnimationEnd) {
    t12 = () => reportAnimationEnd(SPEED_INDEX);
    $[20] = reportAnimationEnd;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const reportSpeedEnd = t12;
  let t13;
  if ($[22] !== activeIndex || $[23] !== exitingIndex) {
    t13 = index_2 => {
      if (exitingIndex === index_2) {
        return "exiting";
      }
      return activeIndex === index_2 ? "active" : "idle";
    };
    $[22] = activeIndex;
    $[23] = exitingIndex;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const phaseOf = t13;
  let t14;
  if ($[25] !== activeIndex || $[26] !== hasStoryPicture || $[27] !== holdSpeed) {
    t14 = index_3 => cn("absolute inset-0 transition-opacity ease-out", {
      "opacity-100": activeIndex === index_3 && (index_3 !== STORY_INDEX || hasStoryPicture) || index_3 === SPEED_INDEX && holdSpeed,
      "opacity-0": !(activeIndex === index_3 && (index_3 !== STORY_INDEX || hasStoryPicture)) && !(index_3 === SPEED_INDEX && holdSpeed),
      "pointer-events-none": activeIndex !== index_3
    });
    $[25] = activeIndex;
    $[26] = hasStoryPicture;
    $[27] = holdSpeed;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const sectionClassName = t14;
  const slideDataAttributes = _temp;
  let t15;
  if ($[29] !== onIntroSequenceComplete || $[30] !== reportIntroEnd) {
    t15 = () => {
      setIsIntroSequenceComplete(true);
      reportIntroEnd();
      onIntroSequenceComplete();
    };
    $[29] = onIntroSequenceComplete;
    $[30] = reportIntroEnd;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const handleIntroSequenceComplete = t15;
  let t16;
  let t17;
  if ($[32] !== activeIndex || $[33] !== hasStoryPicture || $[34] !== storyVisit) {
    t16 = () => {
      if (activeIndex !== STORY_INDEX || !hasStoryPicture) {
        return;
      }
      const timer = window.setTimeout(() => setStoryCoveredGeneration(storyVisit), SECTION_FADE_SECONDS * 1000);
      return () => window.clearTimeout(timer);
    };
    t17 = [activeIndex, hasStoryPicture, storyVisit];
    $[32] = activeIndex;
    $[33] = hasStoryPicture;
    $[34] = storyVisit;
    $[35] = t16;
    $[36] = t17;
  } else {
    t16 = $[35];
    t17 = $[36];
  }
  useEffect(t16, t17);
  const onVideoBlockedRef = useRef(onVideoBlocked);
  let t18;
  if ($[37] !== onVideoBlocked) {
    t18 = () => {
      onVideoBlockedRef.current = onVideoBlocked;
    };
    $[37] = onVideoBlocked;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  useEffect(t18);
  const [playAcceptedVisit, setPlayAcceptedVisit] = useState(null);
  const hasAcceptedPlay = playAcceptedVisit === activeVisitKey;
  const isFirstIntroVisit = activeIndex === INTRO_INDEX && generation[INTRO_INDEX] === 0 && !hasAcceptedPlay;
  const isCountdownArmed = isAwaitingPlayback && isAutoplayChecked;
  let t19;
  if ($[39] !== activeIndex || $[40] !== clockStartMs || $[41] !== hasAcceptedPlay || $[42] !== isCountdownArmed || $[43] !== isFirstIntroVisit || $[44] !== onVideoLoadTimeout) {
    t19 = () => {
      if (!isCountdownArmed) {
        return;
      }
      const timeoutMs = SECTION_VIDEO_LOAD_TIMEOUT_SECONDS * 1000;
      const delayMs = isFirstIntroVisit ? msLeftSince(clockStartMs, timeoutMs) : timeoutMs;
      const timer_0 = window.setTimeout(() => {
        const sectionId = SPARTAN_ENGINE_SECTION_IDS[activeIndex];
        if (!hasAcceptedPlay && playbacksRef.current.get(activeIndex)?.isClipOnHand()) {
          logger.warn(`[engine] ${sectionId} video held back, prompting`);
          onVideoBlockedRef.current(activeIndex);
          return;
        }
        logger.warn(`[engine] ${sectionId} video load timed out, skipped`);
        reportRumWarning("spartan_hero_video_timeout", {
          section: sectionId
        });
        onVideoLoadTimeout();
      }, delayMs);
      return () => window.clearTimeout(timer_0);
    };
    $[39] = activeIndex;
    $[40] = clockStartMs;
    $[41] = hasAcceptedPlay;
    $[42] = isCountdownArmed;
    $[43] = isFirstIntroVisit;
    $[44] = onVideoLoadTimeout;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== activeIndex || $[47] !== activeVisitKey || $[48] !== clockStartMs || $[49] !== hasAcceptedPlay || $[50] !== isCountdownArmed || $[51] !== isFirstIntroVisit || $[52] !== onVideoLoadTimeout) {
    t20 = [isCountdownArmed, isFirstIntroVisit, hasAcceptedPlay, clockStartMs, activeIndex, activeVisitKey, onVideoLoadTimeout];
    $[46] = activeIndex;
    $[47] = activeVisitKey;
    $[48] = clockStartMs;
    $[49] = hasAcceptedPlay;
    $[50] = isCountdownArmed;
    $[51] = isFirstIntroVisit;
    $[52] = onVideoLoadTimeout;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  useEffect(t19, t20);
  let t21;
  if ($[54] !== activeVisitKey || $[55] !== isAwaitingPlayback) {
    t21 = () => {
      if (!isAwaitingPlayback) {
        return;
      }
      const timer_1 = window.setTimeout(() => setCoverFallbackVisit(activeVisitKey), VIDEO_COVER_FALLBACK_SECONDS * 1000);
      return () => window.clearTimeout(timer_1);
    };
    $[54] = activeVisitKey;
    $[55] = isAwaitingPlayback;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== activeIndex || $[58] !== activeVisitKey || $[59] !== isAwaitingPlayback) {
    t22 = [isAwaitingPlayback, activeIndex, activeVisitKey];
    $[57] = activeIndex;
    $[58] = activeVisitKey;
    $[59] = isAwaitingPlayback;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  useEffect(t21, t22);
  let t23;
  if ($[61] !== activeIndex || $[62] !== onVideoBlocked || $[63] !== videoEndedVisit || $[64] !== visitKey) {
    t23 = index_5 => {
      if (index_5 !== activeIndex || videoEndedVisit === visitKey(index_5)) {
        return;
      }
      onVideoBlocked(index_5);
    };
    $[61] = activeIndex;
    $[62] = onVideoBlocked;
    $[63] = videoEndedVisit;
    $[64] = visitKey;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  const handleVideoBlocked = t23;
  let t24;
  let t25;
  if ($[66] !== activeIndex || $[67] !== isPlayPromptShown || $[68] !== storyVisit) {
    t24 = () => {
      if (isPlayPromptShown && activeIndex === STORY_INDEX) {
        setStoryPictureGeneration(storyVisit);
      }
    };
    t25 = [isPlayPromptShown, activeIndex, storyVisit];
    $[66] = activeIndex;
    $[67] = isPlayPromptShown;
    $[68] = storyVisit;
    $[69] = t24;
    $[70] = t25;
  } else {
    t24 = $[69];
    t25 = $[70];
  }
  useEffect(t24, t25);
  let t26;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = new Map();
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const playbacksRef = useRef(t26);
  let t27;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = SPARTAN_ENGINE_SECTION_IDS.map((_, index_6) => playback => {
      const playbacks = playbacksRef.current;
      playbacks.set(index_6, playback);
      return () => {
        if (playbacks.get(index_6) === playback) {
          playbacks.delete(index_6);
        }
      };
    });
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  const registerPlayUnlockFor = t27;
  let t28;
  if ($[73] !== activeIndex || $[74] !== activeVisitKey || $[75] !== onPlayPromptAccepted) {
    t28 = () => {
      playbacksRef.current.forEach((playback_0, index_7) => playback_0.unlock(index_7 === activeIndex));
      setCoverFallbackVisit(null);
      setPlayAcceptedVisit(activeVisitKey);
      onPlayPromptAccepted();
    };
    $[73] = activeIndex;
    $[74] = activeVisitKey;
    $[75] = onPlayPromptAccepted;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  const handlePlay = t28;
  let t29;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = (index_8, isBuffering) => setBufferingIndex(current => {
      if (isBuffering) {
        return index_8;
      }
      return current === index_8 ? null : current;
    });
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  const handleBufferingChange = t29;
  let t30;
  let t31;
  if ($[78] !== isIntroSequenceComplete) {
    t30 = () => {
      if (isIntroSequenceComplete) {
        setHaveBulletsAppeared(true);
      }
    };
    t31 = [isIntroSequenceComplete];
    $[78] = isIntroSequenceComplete;
    $[79] = t30;
    $[80] = t31;
  } else {
    t30 = $[79];
    t31 = $[80];
  }
  useEffect(t30, t31);
  let t32;
  if ($[81] !== className || $[82] !== isLocked) {
    t32 = cn("relative w-full overflow-x-clip", SECTION_MIN_HEIGHT_CLASSNAME, {
      "touch-none": isLocked
    }, className);
    $[81] = className;
    $[82] = isLocked;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.slider
    });
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== sectionClassName) {
    t34 = sectionClassName(INTRO_INDEX);
    $[85] = sectionClassName;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  let t36;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      transitionDuration: `${SECTION_FADE_SECONDS}s`
    };
    t36 = slideDataAttributes(INTRO_INDEX);
    $[87] = t35;
    $[88] = t36;
  } else {
    t35 = $[87];
    t36 = $[88];
  }
  const t37 = generation[INTRO_INDEX];
  const t38 = phaseOf(INTRO_INDEX) === "active";
  const t39 = phaseOf(INTRO_INDEX);
  let t40;
  if ($[89] !== reportExitEnd) {
    t40 = () => reportExitEnd(INTRO_INDEX);
    $[89] = reportExitEnd;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== markVideoEnded || $[92] !== onIntroVideoEnded) {
    t41 = () => {
      markVideoEnded(INTRO_INDEX);
      setCanLoadSpeedMain(true);
      onIntroVideoEnded();
    };
    $[91] = markVideoEnded;
    $[92] = onIntroVideoEnded;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== markPlaybackStarted) {
    t42 = () => markPlaybackStarted(INTRO_INDEX);
    $[94] = markPlaybackStarted;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== handleVideoBlocked) {
    t43 = () => handleVideoBlocked(INTRO_INDEX);
    $[96] = handleVideoBlocked;
    $[97] = t43;
  } else {
    t43 = $[97];
  }
  let t44;
  if ($[98] !== canvasBoxRef || $[99] !== engineAnchorRef || $[100] !== engineAuraVisibilityRef || $[101] !== handleIntroSequenceComplete || $[102] !== introFloatAnimationRef || $[103] !== onIntroFloatCycle || $[104] !== onIntroImageLoaded || $[105] !== onVideoError || $[106] !== onVideoReady || $[107] !== playMode || $[108] !== readyToPlay || $[109] !== shouldRenderFallback || $[110] !== t37 || $[111] !== t38 || $[112] !== t39 || $[113] !== t40 || $[114] !== t41 || $[115] !== t42 || $[116] !== t43) {
    t44 = <IntroEngineReveal introFloatAnimationRef={introFloatAnimationRef} key={t37} isActive={t38} phase={t39} onExitEnd={t40} canvasBoxRef={canvasBoxRef} engineAnchorRef={engineAnchorRef} engineAuraVisibilityRef={engineAuraVisibilityRef} playMode={playMode} shouldRenderFallback={shouldRenderFallback} onIntroVideoEnded={t41} onAnimationEnd={handleIntroSequenceComplete} onFloatCycle={onIntroFloatCycle} onVideoReady={onVideoReady} onVideoPlaying={t42} onVideoError={onVideoError} onVideoBlocked={t43} registerPlayUnlock={registerPlayUnlockFor[INTRO_INDEX]} onImageLoaded={onIntroImageLoaded} readyToPlay={readyToPlay} />;
    $[98] = canvasBoxRef;
    $[99] = engineAnchorRef;
    $[100] = engineAuraVisibilityRef;
    $[101] = handleIntroSequenceComplete;
    $[102] = introFloatAnimationRef;
    $[103] = onIntroFloatCycle;
    $[104] = onIntroImageLoaded;
    $[105] = onVideoError;
    $[106] = onVideoReady;
    $[107] = playMode;
    $[108] = readyToPlay;
    $[109] = shouldRenderFallback;
    $[110] = t37;
    $[111] = t38;
    $[112] = t39;
    $[113] = t40;
    $[114] = t41;
    $[115] = t42;
    $[116] = t43;
    $[117] = t44;
  } else {
    t44 = $[117];
  }
  let t45;
  if ($[118] !== t34 || $[119] !== t44) {
    t45 = <div className={t34} style={t35} {...t36}>{t44}</div>;
    $[118] = t34;
    $[119] = t44;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  let t46;
  if ($[121] !== sectionClassName) {
    t46 = sectionClassName(SPEED_INDEX);
    $[121] = sectionClassName;
    $[122] = t46;
  } else {
    t46 = $[122];
  }
  let t47;
  let t48;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      transitionDuration: `${SECTION_FADE_SECONDS}s`
    };
    t48 = slideDataAttributes(SPEED_INDEX);
    $[123] = t47;
    $[124] = t48;
  } else {
    t47 = $[123];
    t48 = $[124];
  }
  const t49 = generation[SPEED_INDEX];
  const t50 = phaseOf(SPEED_INDEX) === "active";
  const t51 = phaseOf(SPEED_INDEX);
  let t52;
  if ($[125] !== reportExitEnd) {
    t52 = () => reportExitEnd(SPEED_INDEX);
    $[125] = reportExitEnd;
    $[126] = t52;
  } else {
    t52 = $[126];
  }
  let t53;
  if ($[127] !== markVideoEnded) {
    t53 = () => markVideoEnded(SPEED_INDEX);
    $[127] = markVideoEnded;
    $[128] = t53;
  } else {
    t53 = $[128];
  }
  let t54;
  if ($[129] !== handleVideoBlocked) {
    t54 = () => handleVideoBlocked(SPEED_INDEX);
    $[129] = handleVideoBlocked;
    $[130] = t54;
  } else {
    t54 = $[130];
  }
  let t55;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = isBuffering_0 => handleBufferingChange(SPEED_INDEX, isBuffering_0);
    $[131] = t55;
  } else {
    t55 = $[131];
  }
  let t56;
  if ($[132] !== canLoadSpeedMain || $[133] !== handleSpeedPlayback || $[134] !== holdSpeed || $[135] !== isProgressWatched || $[136] !== onBlackModeChange || $[137] !== playMode || $[138] !== reportSpeedEnd || $[139] !== t49 || $[140] !== t50 || $[141] !== t51 || $[142] !== t52 || $[143] !== t53 || $[144] !== t54 || $[145] !== videoProgressRef) {
    t56 = <SpeedKeyMessages key={t49} isActive={t50} phase={t51} index={SPEED_INDEX} videoProgressRef={videoProgressRef} isProgressWatched={isProgressWatched} onExitEnd={t52} playMode={playMode} onAnimationEnd={reportSpeedEnd} onPlaybackStarted={handleSpeedPlayback} onVideoEnded={t53} canLoadMain={canLoadSpeedMain} onVideoBlocked={t54} registerPlayUnlock={registerPlayUnlockFor[SPEED_INDEX]} onBufferingChange={t55} keepLoopPlaying={holdSpeed} onBlackModeChange={onBlackModeChange} />;
    $[132] = canLoadSpeedMain;
    $[133] = handleSpeedPlayback;
    $[134] = holdSpeed;
    $[135] = isProgressWatched;
    $[136] = onBlackModeChange;
    $[137] = playMode;
    $[138] = reportSpeedEnd;
    $[139] = t49;
    $[140] = t50;
    $[141] = t51;
    $[142] = t52;
    $[143] = t53;
    $[144] = t54;
    $[145] = videoProgressRef;
    $[146] = t56;
  } else {
    t56 = $[146];
  }
  let t57;
  if ($[147] !== t46 || $[148] !== t56) {
    t57 = <div className={t46} style={t47} {...t48}>{t56}</div>;
    $[147] = t46;
    $[148] = t56;
    $[149] = t57;
  } else {
    t57 = $[149];
  }
  let t58;
  if ($[150] !== sectionClassName) {
    t58 = sectionClassName(STORY_INDEX);
    $[150] = sectionClassName;
    $[151] = t58;
  } else {
    t58 = $[151];
  }
  let t59;
  let t60;
  if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = {
      transitionDuration: `${SECTION_FADE_SECONDS}s`
    };
    t60 = slideDataAttributes(STORY_INDEX);
    $[152] = t59;
    $[153] = t60;
  } else {
    t59 = $[152];
    t60 = $[153];
  }
  const t61 = generation[STORY_INDEX];
  const t62 = phaseOf(STORY_INDEX) === "active";
  const t63 = phaseOf(STORY_INDEX);
  let t64;
  if ($[154] !== reportExitEnd) {
    t64 = () => reportExitEnd(STORY_INDEX);
    $[154] = reportExitEnd;
    $[155] = t64;
  } else {
    t64 = $[155];
  }
  let t65;
  if ($[156] !== markVideoEnded || $[157] !== onStoryClipsComplete) {
    t65 = () => {
      markVideoEnded(STORY_INDEX);
      onStoryClipsComplete();
    };
    $[156] = markVideoEnded;
    $[157] = onStoryClipsComplete;
    $[158] = t65;
  } else {
    t65 = $[158];
  }
  let t66;
  if ($[159] !== handleVideoBlocked) {
    t66 = () => handleVideoBlocked(STORY_INDEX);
    $[159] = handleVideoBlocked;
    $[160] = t66;
  } else {
    t66 = $[160];
  }
  let t67;
  if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = isBuffering_1 => handleBufferingChange(STORY_INDEX, isBuffering_1);
    $[161] = t67;
  } else {
    t67 = $[161];
  }
  let t68;
  if ($[162] !== canvasBoxRef || $[163] !== engineAnchorRef || $[164] !== engineAuraVisibilityRef || $[165] !== handleStoryPicture || $[166] !== isProgressWatched || $[167] !== onBlackModeChange || $[168] !== onHotspotPopupChange || $[169] !== playMode || $[170] !== preloadStory || $[171] !== reportStoryEnd || $[172] !== shouldRenderFallback || $[173] !== storyBreathingAnimationRef || $[174] !== storyVideoGeneration || $[175] !== t61 || $[176] !== t62 || $[177] !== t63 || $[178] !== t64 || $[179] !== t65 || $[180] !== t66 || $[181] !== videoProgressRef) {
    t68 = <EngineStory shouldRenderFallback={shouldRenderFallback} preloadStory={preloadStory} onPictureReady={handleStoryPicture} key={t61} isActive={t62} phase={t63} index={STORY_INDEX} videoProgressRef={videoProgressRef} isProgressWatched={isProgressWatched} onExitEnd={t64} canvasBoxRef={canvasBoxRef} engineAnchorRef={engineAnchorRef} engineAuraVisibilityRef={engineAuraVisibilityRef} playMode={playMode} onBlackModeChange={onBlackModeChange} playGeneration={storyVideoGeneration} onVideoComplete={t65} onAnimationEnd={reportStoryEnd} storyBreathingAnimationRef={storyBreathingAnimationRef} onHotspotPopupChange={onHotspotPopupChange} onVideoBlocked={t66} registerPlayUnlock={registerPlayUnlockFor[STORY_INDEX]} onBufferingChange={t67} />;
    $[162] = canvasBoxRef;
    $[163] = engineAnchorRef;
    $[164] = engineAuraVisibilityRef;
    $[165] = handleStoryPicture;
    $[166] = isProgressWatched;
    $[167] = onBlackModeChange;
    $[168] = onHotspotPopupChange;
    $[169] = playMode;
    $[170] = preloadStory;
    $[171] = reportStoryEnd;
    $[172] = shouldRenderFallback;
    $[173] = storyBreathingAnimationRef;
    $[174] = storyVideoGeneration;
    $[175] = t61;
    $[176] = t62;
    $[177] = t63;
    $[178] = t64;
    $[179] = t65;
    $[180] = t66;
    $[181] = videoProgressRef;
    $[182] = t68;
  } else {
    t68 = $[182];
  }
  let t69;
  if ($[183] !== t58 || $[184] !== t68) {
    t69 = <div className={t58} style={t59} {...t60}>{t68}</div>;
    $[183] = t58;
    $[184] = t68;
    $[185] = t69;
  } else {
    t69 = $[185];
  }
  const t70 = activeIndex === INTRO_INDEX;
  let t71;
  if ($[186] !== t70) {
    t71 = cn({
      "z-60": t70
    });
    $[186] = t70;
    $[187] = t71;
  } else {
    t71 = $[187];
  }
  let t72;
  if ($[188] !== isClipLoading || $[189] !== t71) {
    t72 = <LoadingVeil variant="clip" isVisible={isClipLoading} className={t71} />;
    $[188] = isClipLoading;
    $[189] = t71;
    $[190] = t72;
  } else {
    t72 = $[190];
  }
  let t73;
  if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = {
      prompt: SPARTAN_ENGINE_HERO_TRACKING.playPrompt,
      button: SPARTAN_ENGINE_HERO_TRACKING.playButton
    };
    $[191] = t73;
  } else {
    t73 = $[191];
  }
  const t74 = SPARTAN_ENGINE_SECTION_IDS[activeIndex];
  let t75;
  if ($[192] !== t74) {
    t75 = {
      eventName: SPARTAN_ENGINE_EVENT_NAME,
      group: SPARTAN_ENGINE_HERO_DATA_GA.section,
      value: t74
    };
    $[192] = t74;
    $[193] = t75;
  } else {
    t75 = $[193];
  }
  const t76 = activeIndex === INTRO_INDEX;
  let t77;
  if ($[194] !== t76) {
    t77 = cn(SECTION_VIDEO_BOX_CLASSNAME, "z-10", {
      "z-60": t76
    });
    $[194] = t76;
    $[195] = t77;
  } else {
    t77 = $[195];
  }
  let t78;
  if ($[196] !== handlePlay || $[197] !== isPlayPromptShown || $[198] !== t75 || $[199] !== t77) {
    t78 = <PlayPrompt isVisible={isPlayPromptShown} onPlay={handlePlay} testIds={t73} dataGA={t75} className={t77} />;
    $[196] = handlePlay;
    $[197] = isPlayPromptShown;
    $[198] = t75;
    $[199] = t77;
    $[200] = t78;
  } else {
    t78 = $[200];
  }
  const t79 = !showSkipButton;
  const t80 = !showSkipButton;
  const t81 = phaseOf(INTRO_INDEX) === "active";
  let t82;
  if ($[201] !== t80 || $[202] !== t81) {
    t82 = cn(HERO_BUTTON_CLASSNAME, "absolute right-1/2 bottom-6 z-20 translate-x-1/2 lg:right-13 lg:bottom-12 lg:translate-x-0", "property-opacity-translate ease-curve duration-500", {
      "pointer-events-none translate-y-1 opacity-0": t80,
      "z-60": t81
    });
    $[201] = t80;
    $[202] = t81;
    $[203] = t82;
  } else {
    t82 = $[203];
  }
  let t83;
  if ($[204] === Symbol.for("react.memo_cache_sentinel")) {
    t83 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.skipButton
    });
    $[204] = t83;
  } else {
    t83 = $[204];
  }
  const t84 = SPARTAN_ENGINE_SECTION_IDS[activeIndex];
  let t85;
  if ($[205] !== t84) {
    t85 = createDataGA({
      eventName: SPARTAN_ENGINE_EVENT_NAME,
      group: SPARTAN_ENGINE_HERO_DATA_GA.section,
      click: true,
      text: SPARTAN_ENGINE_HERO_SKIP_LABEL,
      value: t84
    });
    $[205] = t84;
    $[206] = t85;
  } else {
    t85 = $[206];
  }
  let t86;
  if ($[207] === Symbol.for("react.memo_cache_sentinel")) {
    t86 = <span className="relative">{SPARTAN_ENGINE_HERO_SKIP_LABEL}</span>;
    $[207] = t86;
  } else {
    t86 = $[207];
  }
  let t87;
  if ($[208] !== onSkip || $[209] !== t79 || $[210] !== t82 || $[211] !== t85) {
    t87 = <button type="button" onClick={onSkip} inert={t79} className={t82} {...t83} {...t85}>{t86}</button>;
    $[208] = onSkip;
    $[209] = t79;
    $[210] = t82;
    $[211] = t85;
    $[212] = t87;
  } else {
    t87 = $[212];
  }
  let t88;
  if ($[213] !== t32 || $[214] !== t45 || $[215] !== t57 || $[216] !== t69 || $[217] !== t72 || $[218] !== t78 || $[219] !== t87) {
    t88 = <div className={t32} {...t33}>{t45}{t57}{t69}{t72}{t78}{t87}</div>;
    $[213] = t32;
    $[214] = t45;
    $[215] = t57;
    $[216] = t69;
    $[217] = t72;
    $[218] = t78;
    $[219] = t87;
    $[220] = t88;
  } else {
    t88 = $[220];
  }
  let t89;
  if ($[221] !== showSkipButton) {
    t89 = cn("property-translate ease-curve duration-500", {
      "lg:-translate-y-22": showSkipButton
    });
    $[221] = showSkipButton;
    $[222] = t89;
  } else {
    t89 = $[222];
  }
  let t90;
  if ($[223] !== t89) {
    t90 = <FloatingButton className={t89} />;
    $[223] = t89;
    $[224] = t90;
  } else {
    t90 = $[224];
  }
  const t91 = !isSectionInView;
  const t92 = !haveBulletsAppeared;
  let t93;
  if ($[225] !== t92) {
    t93 = cn("absolute top-1/2 right-2 z-10 -translate-y-1/2 transition-opacity duration-800 lg:right-4", {
      "pointer-events-none opacity-0": t92
    });
    $[225] = t92;
    $[226] = t93;
  } else {
    t93 = $[226];
  }
  let t94;
  if ($[227] !== activeIndex || $[228] !== isRingTrackingClip || $[229] !== onSelectSection || $[230] !== t91 || $[231] !== t93 || $[232] !== videoProgressRef) {
    t94 = <PaginationBullets count={SPARTAN_ENGINE_SECTION_COUNT} activeIndex={activeIndex} progressRef={videoProgressRef} isProgressAnimated={isRingTrackingClip} isProgressPaused={t91} onSelect={onSelectSection} className={t93} />;
    $[227] = activeIndex;
    $[228] = isRingTrackingClip;
    $[229] = onSelectSection;
    $[230] = t91;
    $[231] = t93;
    $[232] = videoProgressRef;
    $[233] = t94;
  } else {
    t94 = $[233];
  }
  let t95;
  if ($[234] !== t88 || $[235] !== t90 || $[236] !== t94) {
    t95 = <>{t88}{t90}{t94}</>;
    $[234] = t88;
    $[235] = t90;
    $[236] = t94;
    $[237] = t95;
  } else {
    t95 = $[237];
  }
  return t95;
};
function _temp(index_4) {
  return createDataAttributes({
    test: SPARTAN_ENGINE_HERO_TRACKING.slide(SPARTAN_ENGINE_SECTION_IDS[index_4])
  });
}